import type { Component } from 'vue';
import { columnHeaderLabel } from '@/utils/columnLabels';
import { NESTED_SCHEDULES_FIELD } from '@/composables/api/scheduleItems';
import type { RawDdexPartyOption } from '@/composables/api/useRawDdexParties';
import type { FilterControlConfig } from '@/types/columnTypes';
import type { TableColumn, TableColumnStaticProps } from '@/types/tableTypes';
import { TRACK_MATCH_STATUS_FILTER_OPTIONS } from '@/constants/trackMatchStatus';
import {
  DISPLAY_STRING_LOOKUP_OPTIONS,
  ID_LOOKUP_OPTIONS,
  type LookupFilterOption,
} from '@/constants/lookupFilterOptions';
import { type AudioFileIssueEnumOption } from '@/constants/audioFileIssueEnums';
import SeverityBadge from '@/components/AudioFileIssues/SeverityBadge.vue';
import TrackRatingBadge from '@/components/AudioFileIssues/TrackRatingBadge.vue';
import TagFilterOption from '@/components/FilterBuilder/TagFilterOption.vue';
import BucketTypeOption from '@/components/FilterBuilder/BucketTypeOption.vue';
import type { TagFilterOptionShape } from '@/composables/useFilterOptions';

/**
 * Criterion shown in the filter dropdown for this options source. The
 * `control` decides which value-input component renders for the rule:
 *  - `multi_combobox` for "is one of" / "is none of" (the array control)
 *  - `single_select` for "is" / "is not" (the scalar control)
 *
 * Mixed-control entries (severity, issue_type, ...) use both kinds, so a
 * single registry entry can drive both an "is" dropdown and an "is one of"
 * dropdown with the same option list and option renderer.
 */
export interface FilterControlCriterion {
  label: string;
  value: string;
  control: 'multi_combobox' | 'single_select';
}

export interface FilterControlRegistryEntry<T = unknown> {
  criteria: FilterControlCriterion[];
  getValue: (option: T) => string;
  getLabel: (option: T) => string;
  filterByQuery: (option: T, query: string) => boolean;
  /**
   * Optional Vue component used to render each option in the dropdown and the
   * trigger pill. Receives `{ value, label }` as props. Use to surface coloured
   * badges (e.g. severity) without duplicating the badge styling in this file.
   */
  optionComponent?: Component;
}

const rawDdexPartiesEntry: FilterControlRegistryEntry<RawDdexPartyOption> = {
  criteria: [
    { label: 'contains any', value: 'contains_any', control: 'multi_combobox' },
    { label: 'contains all', value: 'contains_all', control: 'multi_combobox' },
    { label: 'does not contain any', value: 'not_contains_any', control: 'multi_combobox' },
    { label: 'does not contain all', value: 'not_contains_all', control: 'multi_combobox' },
  ],
  /** Store numeric party id (as string) so the filter sends numbers to the API. */
  getValue: (option) => String(option.id),
  getLabel: (option) => option.name ?? option.party_id,
  filterByQuery: (option, searchQuery) =>
    (option.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
};

/** Static options: each option is the display string (value = label). */
const trackMatchTypesEntry: FilterControlRegistryEntry<string> = {
  criteria: [
    { label: 'contains any', value: 'contains_any', control: 'multi_combobox' },
    { label: 'contains all', value: 'contains_all', control: 'multi_combobox' },
    { label: 'does not contain any', value: 'not_contains_any', control: 'multi_combobox' },
    { label: 'does not contain all', value: 'not_contains_all', control: 'multi_combobox' },
  ],
  getValue: (option) => option,
  getLabel: (option) => option,
  filterByQuery: (option, searchQuery) => option.toLowerCase().includes(searchQuery.toLowerCase()),
};

type TrackMatchStatusFilterOption = (typeof TRACK_MATCH_STATUS_FILTER_OPTIONS)[number];

const trackMatchStatusEntry: FilterControlRegistryEntry<TrackMatchStatusFilterOption> = {
  criteria: [
    { label: 'contains any', value: 'contains_any', control: 'multi_combobox' },
    { label: 'contains all', value: 'contains_all', control: 'multi_combobox' },
    { label: 'does not contain any', value: 'not_contains_any', control: 'multi_combobox' },
    { label: 'does not contain all', value: 'not_contains_all', control: 'multi_combobox' },
  ],
  // Value is the display string, not the id: this picker drives the relation column whose
  // filter field is the keyword `track_match_status.display_string`, so the rule must send
  // display strings (mirrors track_match_types). The track_match_status_id column is a plain
  // numeric filter and no longer uses this entry.
  getValue: (option) => option.display_string,
  getLabel: (option) => option.display_string,
  filterByQuery: (option, searchQuery) =>
    option.display_string.toLowerCase().includes(searchQuery.toLowerCase()),
};

/**
 * Shared entry for the bundled lookup pickers on nested relation columns
 * (account_status, location_status, ...). The rule matches the keyword
 * `<relation>.display_string`, so values are display strings — exactly like
 * trackMatchStatusEntry. Options come from DISPLAY_STRING_LOOKUP_OPTIONS via
 * useFilterOptions; the entry itself is stateless and shared across slugs.
 */
const lookupDisplayStringEntry: FilterControlRegistryEntry<LookupFilterOption> = {
  criteria: [
    { label: 'contains any', value: 'contains_any', control: 'multi_combobox' },
    { label: 'contains all', value: 'contains_all', control: 'multi_combobox' },
    { label: 'does not contain any', value: 'not_contains_any', control: 'multi_combobox' },
    { label: 'does not contain all', value: 'not_contains_all', control: 'multi_combobox' },
  ],
  getValue: (option) => option.display_string,
  getLabel: (option) => option.display_string,
  filterByQuery: (option, searchQuery) =>
    option.display_string.toLowerCase().includes(searchQuery.toLowerCase()),
};

/**
 * Shared entry for bundled lookup pickers on plain integer FK columns
 * (live_track_action_status_id, raw_ddex_track_takedown_status_id) where no
 * nested display_string is indexed. Values are numeric ids sent as strings; the
 * backend converts contains_any/not_contains_any to integer terms queries.
 */
const lookupIdEntry: FilterControlRegistryEntry<LookupFilterOption> = {
  criteria: [
    { label: 'is one of', value: 'contains_any', control: 'multi_combobox' },
    { label: 'is none of', value: 'not_contains_any', control: 'multi_combobox' },
  ],
  getValue: (option) => String(option.id),
  getLabel: (option) => option.display_string,
  filterByQuery: (option, searchQuery) =>
    option.display_string.toLowerCase().includes(searchQuery.toLowerCase()),
};

/**
 * Mixed-control criteria reused by every audio-file-issue enum entry. The
 * dropdown shows "is" / "is not" (scalar) plus "is one of" / "is none of"
 * (multi), matching the operator allowlist the user asked for and keeping
 * the same canonical strings the backend's {@see MysqlScopeQueryBuilder}
 * already understands.
 */
const audioFileIssueEnumCriteria: FilterControlCriterion[] = [
  { label: 'is', value: 'equals', control: 'single_select' },
  { label: 'is not', value: 'not_equals', control: 'single_select' },
  { label: 'is one of', value: 'contains_any', control: 'multi_combobox' },
  { label: 'is none of', value: 'not_contains_any', control: 'multi_combobox' },
];

type EnumOption = AudioFileIssueEnumOption<string>;

/**
 * A registry entry for a bundled enum list. It carries the operators and the
 * value/label accessors only -- the options themselves are resolved by slug in
 * `useFilterOptions`, so nothing is passed in here.
 */
function staticOptionEntry(optionComponent?: Component): FilterControlRegistryEntry<EnumOption> {
  return {
    criteria: audioFileIssueEnumCriteria,
    getValue: (option) => option.value,
    getLabel: (option) => option.label,
    filterByQuery: (option, query) =>
      option.label.toLowerCase().includes(query.toLowerCase()) ||
      option.value.toLowerCase().includes(query.toLowerCase()),
    optionComponent,
  };
}

/** Option shape returned by the searchable-entity option fetchers (accounts, buckets, custom playlists). */
export interface SearchableEntityOption {
  id: number;
  name?: string;
  display_string?: string;
}

/**
 * Entity picker for the LiveTrack `playlist_usages` nested filters (accounts, buckets,
 * custom playlists all share it — only the options source differs). Values are numeric
 * ids sent as strings; the backend nested terms query coerces them back to integers.
 */
const searchableEntityOptionEntry: FilterControlRegistryEntry<SearchableEntityOption> = {
  criteria: [
    { label: 'is one of', value: 'contains_any', control: 'multi_combobox' },
    { label: 'is none of', value: 'not_contains_any', control: 'multi_combobox' },
  ],
  getValue: (option) => String(option.id),
  getLabel: (option) => option.display_string ?? option.name ?? String(option.id),
  filterByQuery: (option, searchQuery) =>
    (option.display_string ?? option.name ?? '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
};

/**
 * Tag picker for every entity with a `tags` relation. The rule targets the
 * nested keyword `tags.display_string`, so values are tag names — mirroring
 * the other relation pickers. Options come pre-grouped by header tag from
 * useFilterOptions ('tags' = non-track tags; 'live_track_tags' = track tags,
 * Live Track only); TagFilterOption renders the kind-colored name with the
 * group name trailing on every row.
 */
const tagsEntry: FilterControlRegistryEntry<TagFilterOptionShape> = {
  criteria: [
    { label: 'contains any', value: 'contains_any', control: 'multi_combobox' },
    { label: 'contains all', value: 'contains_all', control: 'multi_combobox' },
    { label: 'does not contain any', value: 'not_contains_any', control: 'multi_combobox' },
    { label: 'does not contain all', value: 'not_contains_all', control: 'multi_combobox' },
  ],
  getValue: (option) => option.name,
  getLabel: (option) => option.name,
  filterByQuery: (option, searchQuery) => {
    const query = searchQuery.toLowerCase();
    return (
      option.name.toLowerCase().includes(query) ||
      (option.groupName ?? '').toLowerCase().includes(query)
    );
  },
  optionComponent: TagFilterOption,
};

const audioFileIssueSeverityEntry = staticOptionEntry(SeverityBadge);
const audioFileIssueTypeEntry = staticOptionEntry();
const originalBitrateStatusEntry = staticOptionEntry();
// The dropdown shows the same badge the Rating cell shows, so the colour a user
// picks by is the colour they scanned the table with.
const trackRatingEntry = staticOptionEntry(TrackRatingBadge);

/**
 * Id-lookup slugs that draw something beside each option, applied inside the
 * spread below so registration order cannot decide whether the mark survives.
 *
 * Buckets show the same mark the search table draws, so the thing you pick by is
 * the thing you scanned the table with. They have no colour pill of their own.
 */
const ID_LOOKUP_OPTION_COMPONENTS: Record<string, Component> = {
  bucket_type: BucketTypeOption,
};

/**
 * Registry of filter control options sources. Keys are slugs (e.g. from filterControlMappings).
 * Add a new entry here and wire it in useFilterOptions to support another multi-select in filters.
 */
export const filterControlRegistry: Record<string, FilterControlRegistryEntry> = {
  raw_ddex_parties: rawDdexPartiesEntry as FilterControlRegistryEntry,
  track_match_types: trackMatchTypesEntry as FilterControlRegistryEntry,
  track_match_status: trackMatchStatusEntry as FilterControlRegistryEntry,
  audio_file_issue_severity: audioFileIssueSeverityEntry as FilterControlRegistryEntry,
  audio_file_issue_type: audioFileIssueTypeEntry as FilterControlRegistryEntry,
  original_bitrate_status: originalBitrateStatusEntry as FilterControlRegistryEntry,
  track_rating: trackRatingEntry as FilterControlRegistryEntry,
  accounts: searchableEntityOptionEntry as FilterControlRegistryEntry,
  tags: tagsEntry as FilterControlRegistryEntry,
  live_track_tags: tagsEntry as FilterControlRegistryEntry,
  ...Object.fromEntries(
    Object.keys(DISPLAY_STRING_LOOKUP_OPTIONS).map((slug) => [
      slug,
      lookupDisplayStringEntry as FilterControlRegistryEntry,
    ]),
  ),
  ...Object.fromEntries(
    Object.keys(ID_LOOKUP_OPTIONS).map((slug) => {
      const optionComponent = ID_LOOKUP_OPTION_COMPONENTS[slug];
      const entry = optionComponent ? { ...lookupIdEntry, optionComponent } : lookupIdEntry;
      return [slug, entry as FilterControlRegistryEntry];
    }),
  ),
};

/**
 * Per-model, per-field filter control config. Keyed by model name, then field name.
 * This replaces the _filterControlMappings that were previously merged into models.json
 * via extendedModels.json.
 *
 * THE FIELD NAME MUST BE A COLUMN models.json ACTUALLY EMITS. This map only decorates
 * a column `buildEntityColumns` already produced; it cannot create one. A key that
 * names no column is silently inert -- the picker never appears, and a test asserting
 * `filterControlMappings.Model?.field?.optionsSource` still passes, because it reads
 * back this object rather than the column. That shipped once, for `Bucket.bucket_type`.
 *
 * `models.json` is generated from each model's `casts()`, so a field that is indexed
 * for search but not cast is NOT a column. Buckets are the worked example: the search
 * document carries a `bucket_type` slug, but the backend deliberately does not cast or
 * append it, so the only real column is the integer `bucket_type_id`.
 *
 * So when a lookup table backs the field, filter the `<name>_id` column through
 * ID_LOOKUP_OPTIONS in `@/constants/lookupFilterOptions`. One entry there registers
 * the slug in filterControlRegistry and in the static options resolver at once, gives
 * you set-membership operators that are safe on nested paths, and lets
 * `idLookupDisplayString` render a label instead of a raw integer in the table.
 * Reach for `extraFilterColumns` only when there is genuinely no column to decorate,
 * as with a dotted nested path like `buckets.bucket_type_id`.
 *
 * Assert new mappings against `buildEntityColumns(model)`, never against this object.
 */
export const filterControlMappings: Record<string, Record<string, FilterControlConfig>> = {
  LiveTrack: {
    tags: { optionsSource: 'live_track_tags', placeholder: 'Select tags...' },
    // Only the relation column gets the curated status picker. track_match_status_id stays a
    // plain numeric FK filter like every other *_id column. The picker emits status display
    // strings to match the keyword `track_match_status.display_string` field this column
    // filters on (see trackMatchStatusEntry.getValue).
    track_match_status: { optionsSource: 'track_match_status', placeholder: 'Select match status...' },
    track_match_types: { optionsSource: 'track_match_types', placeholder: 'Select match types...' },
    match_sender_party_ids: { optionsSource: 'raw_ddex_parties', placeholder: 'Select sending parties...' },
    match_sent_on_behalf_of_party_ids: { optionsSource: 'raw_ddex_parties', placeholder: 'Select on-behalf-of parties...' },
    lyrics_check_status: { optionsSource: 'lyrics_check_status', placeholder: 'Select lyrics status...' },
    staged_track_source: { optionsSource: 'staged_track_source', placeholder: 'Select source...' },
    parental_advisory_type: { optionsSource: 'parental_advisory_type', placeholder: 'Select advisory...' },
  },
  AudioFileIssue: {
    severity: { optionsSource: 'audio_file_issue_severity', placeholder: 'Select severity...' },
    issue_type: { optionsSource: 'audio_file_issue_type', placeholder: 'Select issue type...' },
    rating: { optionsSource: 'track_rating', placeholder: 'Select ratings...' },
    original_status: { optionsSource: 'original_bitrate_status', placeholder: 'Select status...' },
  },
  Account: {
    account_status: { optionsSource: 'account_status', placeholder: 'Select account status...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  Album: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  Artist: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  Bucket: {
    // The integer FK, not the `bucket_type` slug: see the note above.
    bucket_type_id: { optionsSource: 'bucket_type', placeholder: 'Select bucket type...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  Contact: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  Location: {
    location_status: { optionsSource: 'location_status', placeholder: 'Select location status...' },
    region: { optionsSource: 'region', placeholder: 'Select regions...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  LiveTrackAction: {
    // No nested relation is indexed for the action status — the picker filters the
    // integer FK column by id (lookupIdEntry).
    live_track_action_status_id: { optionsSource: 'live_track_action_status', placeholder: 'Select action status...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  LocationZone: {
    location_zone_status: { optionsSource: 'location_zone_status', placeholder: 'Select zone status...' },
    content_style: { optionsSource: 'content_style', placeholder: 'Select content style...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  LocationZoneCustomPlaylist: {
    location_zone_custom_playlist_status: { optionsSource: 'location_zone_custom_playlist_status', placeholder: 'Select status...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  LocationZoneCustomPlaylistSchedule: {
    // Integer FK picker (lookupIdEntry). It also makes the table and the card print
    // the type's name instead of its id.
    custom_playlist_schedule_type_id: { optionsSource: 'custom_playlist_schedule_type', placeholder: 'Select schedule type...' },
  },
  LocationZonePlayer: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
    // Integer FK picker (lookupIdEntry): filters the indexed brand_id by id.
    brand_id: { optionsSource: 'player_brand', placeholder: 'Select brand...' },
    location_zone_player_stream_mode: { optionsSource: 'location_zone_player_stream_mode', placeholder: 'Select stream mode...' },
    crossfade_type: { optionsSource: 'crossfade_type', placeholder: 'Select crossfade type...' },
    player_type: { optionsSource: 'player_type', placeholder: 'Select player type...' },
    player_server: { optionsSource: 'player_server', placeholder: 'Select player server...' },
  },
  ContactLoginStation: {
    contact_login_station_source_type: { optionsSource: 'contact_login_station_source_type', placeholder: 'Select source type...' },
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  ContactLoginCustomPlaylist: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  RecordLabel: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  Template: {
    tags: { optionsSource: 'tags', placeholder: 'Select tags...' },
  },
  RawDdexTrack: {
    // Same id-based picker treatment as live_track_action_status_id — only the
    // integer FK is indexed for takedown status.
    raw_ddex_track_takedown_status_id: { optionsSource: 'raw_ddex_track_takedown_status', placeholder: 'Select takedown status...' },
  },
};

/**
 * Filter-only columns appended to the filter builder's column list per entity (see
 * `usePersistentTable.columnsForFilter`). They are never part of the table column set —
 * dotted keys split into `{column, field}` in RuleRow, producing nested ES rules like
 * `{column: 'playlist_usages', field: 'account_id', criterion: 'contains_any', value: [...]}`.
 */
function filterOnlyColumn(
  id: string,
  key: string,
  header: string,
  filterControl?: FilterControlConfig,
  dataType = 'number',
): TableColumnStaticProps {
  return {
    id,
    key,
    field: key,
    header,
    dataType,
    isFilled: false,
    isProhibited: false,
    isUnique: false,
    isSortable: false,
    isFilterable: true,
    ...(filterControl ? { filterControl } : {}),
  };
}

/**
 * One field of a custom list's nested schedules, listed in the "Schedule" group. The
 * fields match LocationZoneCustomPlaylist::nestedRuleGroupFields() in tabasco, which
 * refuses a schedule rule on any other field. The dates are typed `Date` so they get
 * the date picker. date, end_date, recurring_start_date and recurring_end_date hold a
 * day without a time, and models.json types them as strings.
 * effective_start_datetime and effective_end_datetime are date-times. A "between"
 * whose To is a plain day still includes later times on that day, because the
 * backend's Elasticsearch range rounds a date-only upper bound up to the end of the day.
 */
function scheduleFilterColumn(
  field: string,
  dataType: 'Date' | 'string' | 'number' | 'boolean',
  header = columnHeaderLabel(field),
  filterControl?: FilterControlConfig,
): TableColumnStaticProps {
  return {
    ...filterOnlyColumn(
      `custom_list_schedule_${field}`,
      `location_zone_custom_playlist_schedules.${field}`,
      header,
      filterControl,
      dataType,
    ),
    filterGroup: 'Schedule',
    perScheduleNested: true,
  };
}

export const extraFilterColumns: Record<string, TableColumnStaticProps[]> = {
  // Every nested reference to a bucket carries bucket_type_id as well as the slug
  // (Bucket::nestedMapping in tabasco), so the zone filter uses the same id lookup
  // the Bucket page does.
  //
  // Named for the hop it makes. A zone has no type of its own; it has buckets that
  // each have one, so a bare "Bucket Type" would read as a zone attribute.
  //
  // It does not promise the zone HAS buckets either. "is none of Dynamic" compiles
  // to must_not around the nested clause, and a zone with no nested buckets cannot
  // match the inner query, so the negation admits it. Measured locally: 90 of the
  // 120 zones that filter returns have no buckets at all. "Bucket Types In Zone is
  // none of Dynamic" is true of an empty zone; "Has Bucket Of Type" was not.
  //
  // The plain "Buckets" column stays a free-text display_string filter.
  LocationZone: [
    filterOnlyColumn('location_zone_bucket_type', 'buckets.bucket_type_id', 'Bucket Types In Zone', {
      optionsSource: 'bucket_type',
      placeholder: 'Select bucket type...',
    }),
  ],
  // "Start date is after Oct 31" and "start date is before Nov 8" in one "and" group
  // must be met by the same schedule. The zone is filtered by id, because a list's
  // nested schedules carry the zone's id but not its name.
  LocationZoneCustomPlaylist: [
    scheduleFilterColumn('date', 'Date'),
    scheduleFilterColumn('end_date', 'Date'),
    scheduleFilterColumn('recurring_start_date', 'Date'),
    scheduleFilterColumn('recurring_end_date', 'Date'),
    scheduleFilterColumn('effective_start_datetime', 'Date'),
    scheduleFilterColumn('effective_end_datetime', 'Date'),
    scheduleFilterColumn('time', 'string'),
    scheduleFilterColumn('end_time', 'string'),
    scheduleFilterColumn('delimited_day_ids', 'string', 'Days'),
    scheduleFilterColumn('custom_playlist_schedule_type_id', 'number', 'Schedule Type', {
      optionsSource: 'custom_playlist_schedule_type',
      placeholder: 'Select schedule type...',
    }),
    // A silence schedule is a Message schedule with this flag set, not a type of its own.
    // So "Schedule Type is Message" finds both, and "Silence is true" finds the silences.
    scheduleFilterColumn('silence', 'boolean', 'Silence'),
    scheduleFilterColumn('location_zone_id', 'number', 'Zone ID'),
    // True for a schedule on a zone that is neither the list's zone nor one of its
    // subscribers, the schedules the card's red count box counts. The backend works it
    // out when it indexes the list (LocationZoneCustomPlaylistSchedule::isOnOtherZoneThan),
    // so "is true" finds the lists to fix and the card shows only those schedules.
    scheduleFilterColumn('is_on_other_zone', 'boolean', 'On Other Zone'),
  ],
  LiveTrack: [
    // List-level filtering goes through the free-text "Playlist Usages" filter
    // (playlist_usages.display_string contains) — no per-bucket / per-list pickers.
    filterOnlyColumn('playlist_usages_account', 'playlist_usages.account_id', 'Used By Account', {
      optionsSource: 'accounts',
      placeholder: 'Select accounts...',
    }),
  ],
};

/**
 * The schedule fields a custom list search can filter and sort on, in the order the
 * "Schedule" filter group lists them. The column chooser places a schedule column it
 * turns on by this order.
 */
export const CUSTOM_LIST_SCHEDULE_FIELDS: readonly string[] = extraFilterColumns.LocationZoneCustomPlaylist.map(
  (column) => column.key.slice(NESTED_SCHEDULES_FIELD.length + 1),
);

/**
 * The schedule field a column key names, such as "date" for
 * `location_zone_custom_playlist_schedules.date`, or null when the key is not one of
 * the schedule fields. The "schedules" column's own key ends in `.display_string`, so
 * it reads as null.
 */
export function customListScheduleFieldOf(key: string): string | null {
  const prefix = `${NESTED_SCHEDULES_FIELD}.`;
  if (!key.startsWith(prefix)) return null;
  const field = key.slice(prefix.length);
  return CUSTOM_LIST_SCHEDULE_FIELDS.includes(field) ? field : null;
}

/**
 * The schedule fields as table columns on the custom list search. Each one copies its
 * "Schedule" filter column, keeps its id, and can be sorted. It is hidden until a user
 * turns it on in the column chooser.
 *
 * The copies cannot be filtered. The filter builder keeps using extraFilterColumns, so
 * the filter picker does not change. The cell draws one value per schedule the row
 * shows. See ScheduleFieldCell.
 *
 * Their data type is "display", like the Play column's, because they are table columns
 * only. So the record pane, the export, the group-by choices and the card summary leave
 * them out, as they leave out every display column. ColumnMapping draws them before it
 * reads the data type.
 */
export const customListScheduleTableColumns: TableColumnStaticProps[] = extraFilterColumns.LocationZoneCustomPlaylist.map(
  (filterColumn) => {
    const column: TableColumnStaticProps = {
      ...filterColumn,
      dataType: 'display',
      isSortable: true,
      isFilterable: false,
      hiddenByDefault: true,
    };
    // Only the filter picker reads these.
    delete column.filterGroup;
    delete column.perScheduleNested;
    delete column.filterControl;
    return column;
  },
);

/** Registry slug for multi-combobox options (`filterControlRegistry`), if the column was wired via `filterControlMappings`. */
export function resolveFilterControlOptionsSource(
  column: TableColumn | undefined
): string | undefined {
  return column?.filterControl?.optionsSource;
}
